import React, { useState, useEffect } from 'react';
import './index.scss';

interface Endpoint {
  method: string;
  path: string;
  description: string;
  category: string;
  parameters?: {
    name: string;
    type: string;
    dataType: string;
    description: string;
    required?: boolean;
    defaultValue?: unknown;
  }[];
  response?: unknown;
}

interface Category {
  id: string;
  name: string;
}

interface ApiData {
  endpoints: Endpoint[];
  categories: Category[];
}

interface StatsData {
  totalEndpoints: number;
  categories: Record<string, number>;
  methods: Record<string, number>;
}

const ApiDocs: React.FC = () => {
  const [apiData, setApiData] = useState<ApiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentCategory, setCurrentCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState<StatsData | null>(null);

  const API_BASE =
    process.env.NODE_ENV === 'production'
      ? `${window.location.origin}/api/docs`
      : 'http://localhost:3002/api/docs';

  useEffect(() => {
    const loadApiData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}`);
        const data = await response.json();

        if (data.success) {
          setApiData(data.data);
        } else {
          setError('获取API文档失败');
        }
      } catch {
        setError('网络请求失败，请检查后端服务是否运行');
      } finally {
        setLoading(false);
      }
    };

    const loadStats = async () => {
      try {
        const response = await fetch(`${API_BASE}/stats`);
        const data = await response.json();

        if (data.success) {
          setStats(data.data);
        }
      } catch {
        console.error('获取统计信息失败:');
      }
    };

    loadApiData();
    loadStats();
  }, []);

  const getFilteredEndpoints = () => {
    if (!apiData) return [];

    let endpoints = apiData.endpoints;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      endpoints = endpoints.filter(
        (endpoint) =>
          endpoint.path.toLowerCase().includes(query) ||
          endpoint.description.toLowerCase().includes(query)
      );
    }

    if (currentCategory !== 'all') {
      endpoints = endpoints.filter(
        (endpoint) => endpoint.category === currentCategory
      );
    }

    return endpoints;
  };

  const renderStats = () => {
    if (!stats) return null;

    return (
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">{stats.totalEndpoints}</div>
          <div className="stat-label">总接口数</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {Object.keys(stats.categories).length}
          </div>
          <div className="stat-label">分类数量</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.methods.GET}</div>
          <div className="stat-label">GET接口</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.methods.POST}</div>
          <div className="stat-label">POST接口</div>
        </div>
      </div>
    );
  };

  const renderCategoryTabs = () => {
    if (!apiData) return null;

    return (
      <div className="category-tabs">
        <div
          className={`category-tab ${currentCategory === 'all' ? 'active' : ''}`}
          onClick={() => setCurrentCategory('all')}
        >
          全部接口
        </div>
        {apiData.categories.map((category) => (
          <div
            key={category.id}
            className={`category-tab ${currentCategory === category.id ? 'active' : ''}`}
            onClick={() => setCurrentCategory(category.id)}
          >
            {category.name}
          </div>
        ))}
      </div>
    );
  };

  const renderEndpoints = () => {
    const endpoints = getFilteredEndpoints();

    if (endpoints.length === 0) {
      return <div className="error">没有找到匹配的接口</div>;
    }

    return endpoints.map((endpoint) => (
      <div
        key={`${endpoint.method}-${endpoint.path}`}
        className="endpoint-card"
      >
        <div className="endpoint-header">
          <span
            className={`method-badge method-${endpoint.method.toLowerCase()}`}
          >
            {endpoint.method}
          </span>
          <span className="endpoint-path">{endpoint.path}</span>
        </div>
        <div className="endpoint-description">{endpoint.description}</div>

        {endpoint.parameters && endpoint.parameters.length > 0 && (
          <div className="parameters-section">
            <div className="parameters-title">参数:</div>
            {endpoint.parameters.map((param) => (
              <div key={param.name} className="parameter-item">
                <div className="parameter-header">
                  <span className="parameter-name">
                    {param.name}
                    {param.required && <span className="required-mark">*</span>}
                  </span>
                  <span className="parameter-type">{param.dataType}</span>
                </div>
                <div className="parameter-description">
                  <span className="param-type-badge">{param.type}</span>
                  {param.description}
                  {param.defaultValue !== undefined && (
                    <span className="default-value">
                      默认值: {String(param.defaultValue)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {endpoint.response != null && (
          <div className="parameters-section">
            <div className="parameters-title">返回格式:</div>
            <pre className="response-format">
              {JSON.stringify(endpoint.response, null, 2)}
            </pre>
          </div>
        )}
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="api-docs-container">
        <div className="loading">正在加载API文档...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="api-docs-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="api-docs-container">
      <div className="container">
        <div className="header">
          <h1>个人博客API文档</h1>
          <p>基于Node.js + Express的工程化后端服务接口文档</p>
        </div>

        {renderStats()}

        <div className="search-box">
          <input
            type="text"
            className="search-input"
            placeholder="搜索接口..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {renderCategoryTabs()}

        <div className="endpoint-list">{renderEndpoints()}</div>
      </div>
    </div>
  );
};

export default ApiDocs;
